import { useRef } from "react";
import { ArrowRight } from "lucide-react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import useScrollSpy from "@/lib/useScrollSpy";
import { Reveal } from "@/components/Reveal";
import PageHero from "@/components/PageHero";
import Seo from "@/components/Seo";
import { STANCOR_PATH } from "@/components/SpotlightSection";
import SectorGallery from "@/components/SectorGallery";
import HEROES from "@/lib/pageHeroes.json";


const jumpTo = (id) => {
  const el = document.getElementById(id);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -120, duration: 1.2 });
  else el.scrollIntoView({ behavior: "smooth", block: "start" });
};

function SectorImage({ src, alt, index }) {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const y = useTransform(scrollYProgress, [0, 1], ["-7%", "7%"]);

  return (
    <div ref={ref} className="relative h-[320px] md:h-[480px] overflow-hidden border border-border" style={{ borderRadius: "var(--radius)" }}>
      <motion.img style={{ y, scale: 1.14 }} src={src} alt={alt} className="h-full w-full object-cover" />
      <div className="absolute inset-0 bg-gradient-to-t from-background/65 via-transparent to-transparent" />
      <span className="absolute top-5 start-6 font-head text-6xl md:text-7xl text-white/25">{String(index + 1).padStart(2, "0")}</span>
    </div>
  );
}

export default function Sectors() {
  const { t, L } = useLang();
  const site = useSite();
  const sectors = site?.sectors || [];
  const ventures = site?.ventures || [];
  const spotlight = site?.settings?.spotlight;
  const spotlightSectors =
    spotlight?.enabled && spotlight?.show_sector_chips !== false
      ? (spotlight.sector_ids || "").split(",").map((x) => x.trim()).filter(Boolean)
      : [];
  const active = useScrollSpy(sectors.map((s) => `sector-${s.id}`));

  return (
    <div data-testid="sectors-page">
      <Seo page="sectors" />
      <PageHero eyebrow={t("sectors_detail_eyebrow")} title={t("sectors_detail_title")} sub={t("sectors_sub")} image={HEROES["sectors"]} />

      <div className="mx-auto max-w-7xl px-5 sm:px-8 py-20 md:py-28 grid gap-14 lg:grid-cols-12">
        <aside className="hidden lg:col-span-3 lg:block">
          <div className="sticky top-28" data-testid="sector-rail">
            <p className="eyebrow mb-6">{t("sectors_index")}</p>
            <nav className="space-y-1">
              {sectors.map((sec, i) => {
                const id = `sector-${sec.id}`;
                const isActive = active === id;
                return (
                  <button
                    key={sec.id}
                    type="button"
                    data-testid={`sector-rail-link-${sec.id}`}
                    onClick={() => jumpTo(id)}
                    className={`flex w-full items-center gap-3 border-s-2 py-2.5 ps-4 text-start text-sm transition-all duration-300 ${
                      isActive ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"
                    }`}
                  >
                    <span className="text-[10px] tabular-nums opacity-60">{String(i + 1).padStart(2, "0")}</span>
                    <span className="leading-snug">{L(sec.name)}</span>
                  </button>
                );
              })}
            </nav>
          </div>
        </aside>

        <div className="lg:col-span-9 space-y-24 md:space-y-32">
          {sectors.map((sec, i) => {
            const related = ventures.filter((v) => (sec.related_ventures || []).includes(v.id));
            return (
              <article key={sec.id} id={`sector-${sec.id}`} data-testid={`sector-detail-${sec.id}`} className="scroll-mt-28">
                <Reveal>
                  <SectorImage src={sec.image} alt={L(sec.name)} index={i} />
                </Reveal>
                <Reveal delay={0.1}>
                  <h2 className="mt-9 font-head text-3xl md:text-4xl lg:text-5xl leading-[1.08]">{L(sec.name)}</h2>
                  {spotlightSectors.includes(sec.id) && (
                    <Link
                      to={STANCOR_PATH}
                      data-testid={`sector-spotlight-chip-${sec.id}`}
                      className="mt-4 inline-flex items-center gap-2 rounded-full border border-primary/50 bg-primary/5 px-3.5 py-1.5 text-[11px] font-semibold text-primary transition-colors hover:bg-primary/10"
                    >
                      {spotlight?.logo ? <img src={spotlight.logo} alt="" className="h-4 w-4 rounded object-contain" /> : null}
                      {t("spotlight_chip")}
                    </Link>
                  )}
                  <p className="mt-5 max-w-2xl text-muted-foreground text-base sm:text-lg leading-relaxed">{L(sec.description)}</p>
                </Reveal>

                {(sec.metrics || []).length > 0 && (
                  <Reveal delay={0.16}>
                    <div className="mt-10 grid grid-cols-3 border-y border-border" data-testid={`sector-metrics-${sec.id}`}>
                      {sec.metrics.map((m, mi) => (
                        <div key={mi} className={`py-6 ${mi > 0 ? "border-s border-border ps-5" : "pe-5"}`}>
                          <p className="font-head text-2xl md:text-4xl text-primary leading-none">{m.value}</p>
                          <p className="mt-2 text-[11px] sm:text-xs tracking-wider uppercase text-muted-foreground">{L(m.label)}</p>
                        </div>
                      ))}
                    </div>
                  </Reveal>
                )}

                <Reveal delay={0.18}>
                  <SectorGallery sectorId={sec.id} items={sec.gallery} />
                </Reveal>

                <Reveal delay={0.2}>
                  <p className="eyebrow mt-9 mb-4">{t("sectors_capabilities")}</p>
                  <div className="flex flex-wrap gap-2">
                    {(sec.capabilities || []).map((cap, ci) => (
                      <span key={ci} className="rounded-full border border-border bg-card/60 px-4 py-1.5 text-xs text-foreground/80 transition-colors hover:border-primary/60 hover:text-primary">
                        {L(cap)}
                      </span>
                    ))}
                  </div>
                </Reveal>

                {related.length > 0 && (
                  <Reveal delay={0.24}>
                    <p className="eyebrow mt-9 mb-4">{t("sectors_related")}</p>
                    <div className="flex flex-wrap gap-3">
                      {related.map((v) => (
                        <Link
                          key={v.id}
                          to="/joint-ventures"
                          data-testid={`sector-related-jv-${v.id}`}
                          className="group inline-flex items-center gap-3 border border-border bg-card/40 px-5 py-3 text-sm transition-colors hover:border-primary/60"
                          style={{ borderRadius: "var(--radius)" }}
                        >
                          <span className="font-head text-lg">{v.name}</span>
                          <span className="text-xs text-muted-foreground">{v.region}</span>
                          <ArrowRight className="h-3.5 w-3.5 text-primary transition-transform duration-300 group-hover:translate-x-1 rtl:rotate-180 rtl:group-hover:-translate-x-1" />
                        </Link>
                      ))}
                    </div>
                  </Reveal>
                )}
              </article>
            );
          })}
        </div>
      </div>
    </div>
  );
}
